import { createSteeringURL } from '@/api'
import { steeringAuthInit } from '@/stores/auth'
import { createBaseAPIStore } from '@/stores/util'
import { ImageFrameSpec, RadioSettings } from '@/types'
import { useLocalTime } from '@/util'
import { computed, ComputedRef, MaybeRefOrGetter, Ref, toValue, WritableComputedRef } from 'vue'

export const useRadioSettingsStore = createBaseAPIStore<RadioSettings>(
  'radio-settings',
  createSteeringURL.prefix('settings'),
  steeringAuthInit,
  { syncOnAuth: true },
)

export function useCurrentRadioSettings() {
  const radioStore = useRadioSettingsStore()
  return computed<RadioSettings | null>(() => radioStore.items?.[0] ?? null)
}

export function useRadioLanguageParameter() {
  const radioSettings = useCurrentRadioSettings()
  return computed(() => {
    const primaryLanguage = radioSettings?.value?.i18n?.primaryLanguage ?? 'de'
    const supportedLanguages = radioSettings?.value?.i18n?.supportedLanguages ?? []
    const languages = supportedLanguages.filter((lang) => lang !== primaryLanguage)
    languages.unshift(primaryLanguage)
    return languages.join(',')
  })
}

export function useImageRequirements(
  key: keyof RadioSettings['fieldRequirements'],
  radioSettings?: MaybeRefOrGetter<RadioSettings | null>,
) {
  radioSettings = radioSettings ?? useCurrentRadioSettings()
  return computed(() => {
    const requirements = toValue(radioSettings)?.fieldRequirements[key]
    if (!requirements) return null
    if (!('frame' in requirements)) return null
    return { frame: requirements.frame as Required<ImageFrameSpec> }
  })
}

export function useLocalRadioTime(time: Ref<string>): WritableComputedRef<string>
export function useLocalRadioTime(time: MaybeRefOrGetter<string>): ComputedRef<string>
export function useLocalRadioTime(time: MaybeRefOrGetter<string>) {
  const radioSettings = useCurrentRadioSettings()
  const timezone = computed(() => radioSettings.value?.station.localTimezone ?? 'Europe/Vienna')
  return useLocalTime(time, timezone)
}
